import capitalize from 'lodash/capitalize';
import trimEnd from 'lodash/trimEnd';
import { displayDateTz, DATE_FORMAT_TRUNC_YEAR } from 'utils/date';

import {
  DelayNodeData,
  DelayUsingProperty,
  SkipDaysOfWeek,
  TimeOfDay,
} from 'modules/workflows/types';
import { TextSegment, TextSegmentConfig } from 'ui/workflows/flowchart/types';

/* ------------------------------- DATE / TIME STRING HELPERS ------------------------------ */

const MINS_IN_HR = 60;
const HRS_IN_DAY = 24;
const AMPM_HRS = 12;
const SHOW_TOOLTIP_LINE_LENGTH = 26;

/**
 * Gets the full time string
 * @example
 * getTimeString({ delayDays: 3, delayHours: 0, delayMinutes: 5 }) // '3 days, 0 hours, 5 minutes'
 */
export const getDelayTimeString = ({
  delayDays = 0,
  delayHours = 0,
  delayMinutes = 0,
}: Pick<DelayNodeData, 'delayDays' | 'delayHours' | 'delayMinutes'>): string => {
  const time = {
    day: delayDays,
    hour: delayHours,
    minute: delayMinutes,
  };

  if (time.minute >= MINS_IN_HR) {
    time.hour += Math.floor(time.minute / MINS_IN_HR);
    time.minute %= MINS_IN_HR;
  }

  if (time.hour >= HRS_IN_DAY) {
    time.day += Math.floor(time.hour / HRS_IN_DAY);
    time.hour %= HRS_IN_DAY;
  }

  return Object.entries(time)
    .map(([delayType, num]) => `${num || 0} ${delayType}${num === 1 ? '' : 's'}`)
    .join(', ');
};

/**
 * Gets the time of day string
 * @example
 * getTimeString({ hour: 15, minute: 30 }) // '3:30 pm'
 */
export const getTimeOfDayString = ({
  minute = 0,
  hour = 0,
}: Pick<TimeOfDay, 'minute' | 'hour'>) => {
  const minuteStr = minute < 10 ? `0${minute}` : `${minute}`;
  const hourHand = hour % AMPM_HRS;
  const hourStr = hourHand === 0 ? '12' : hourHand;
  const ampm = hour < AMPM_HRS ? 'a.m.' : 'p.m.';

  return `${hourStr}:${minuteStr} ${ampm}`;
};

/* ------------------------------- TEXT SEGMENTS ------------------------------ */

/** Gets the text string for `delayDays, delayHours, delayMinutes` */
const getDelayForText = ({
  delayDays = 0,
  delayHours = 0,
  delayMinutes = 0,
}: DelayNodeData): string => getDelayTimeString({ delayMinutes, delayHours, delayDays });

/** Gets the text string for `delayUsingProperty` */
const getDelayUsingPropertyText = (delayUsingProperty: DelayUsingProperty): string => {
  const { delayIsAdditive, field } = delayUsingProperty;
  return `${delayIsAdditive ? 'after' : 'before'} ${trimEnd(field)}`;
};

/** Gets the text string for `timeOfDay` */
const getTimeOfDayText = ({ timestamp, hour, minute }: TimeOfDay): string => {
  const timestampText = timestamp ? `${displayDateTz(timestamp, DATE_FORMAT_TRUNC_YEAR)}, ` : '';
  const timeOfDayText = getTimeOfDayString({ hour, minute });
  return `Extend to ${timestampText}${timeOfDayText}`;
};

/** Gets the text string for `skipDaysOfWeek` */
const getSkipDaysText = (skipDaysOfWeek: SkipDaysOfWeek): string => {
  const daysSkipped = Object.entries(skipDaysOfWeek).reduce(
    (acc: string[], [key, value]) => (value === true ? [...acc, `${capitalize(key)}s`] : acc),
    [],
  );
  return `Skip on: ${daysSkipped.join(', ')}`;
};

/* ------------------------------- BUILD TEXT SEGMENTS FOR NODE ------------------------------ */
/** Gets the additional text segments for delay description */
const getAdditionalSegments = ({
  delayUsingProperty,
  timeOfDay,
  skipDaysOfWeek,
}: DelayNodeData): TextSegment[] => {
  const additionalText = [];
  if (delayUsingProperty) additionalText.push(getDelayUsingPropertyText(delayUsingProperty));
  if (timeOfDay) additionalText.push(getTimeOfDayText(timeOfDay));
  if (skipDaysOfWeek && Object.values(skipDaysOfWeek).find((val) => val === true))
    additionalText.push(getSkipDaysText(skipDaysOfWeek));

  const firstSegment = additionalText[0];
  const showTooltipText =
    additionalText.length > 1 || firstSegment?.length > SHOW_TOOLTIP_LINE_LENGTH;

  return [
    {
      text: firstSegment,
      ...(showTooltipText && { tooltipText: additionalText.join('\n') }),
    },
  ];
};

/** Gets the text segments for delay description */
export const getDelayTextSegments = (nodeData: DelayNodeData): TextSegment[][] =>
  [[{ text: getDelayForText(nodeData) }], getAdditionalSegments(nodeData)].filter(
    (item) => item[0]?.text,
  );

export const getTextSegments = (nodeData: DelayNodeData): { nodeDescBottom: TextSegmentConfig } => {
  return {
    nodeDescBottom: { isTruncated: true, segments: getDelayTextSegments(nodeData) },
  };
};
